import {
  EnvironmentProviders,
  ErrorHandler,
  Injectable,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import type { PostHogConfig } from 'posthog-js';
import { environment } from '../../environments/environment';
import { AnalyticsService } from './analytics.service';
import { ChunkReloadRecovery } from '../updates/chunk-reload';
import { ANALYTICS_EVENTS } from './events';
import { PosthogService } from './posthog.service';
import type { ConsentListener } from '@ricohproducts/consent';
import { ConsentService } from '@ricohproducts/consent/angular';
import { locationCarriesSecrets, scrubEventUrls, scrubRecordedRequest } from './url-privacy';

/**
 * Sends errors Angular catches (they never reach window.onerror, so the SDK's
 * capture_exceptions can't see them) to PostHog error tracking.
 */
@Injectable()
export class AnalyticsErrorHandler implements ErrorHandler {
  private readonly analytics = inject(AnalyticsService);
  private readonly chunkReload = inject(ChunkReloadRecovery);

  handleError(error: unknown): void {
    // A stale lazy chunk after a deploy: reload onto the new build instead of
    // reporting. A repeat inside the guard window falls through and is reported.
    if (this.chunkReload.recover(error)) {
      console.debug('[chunk-reload] stale chunk after a deploy: reloading');
      return;
    }
    try {
      const err = error instanceof Error ? error : new Error(String(error));
      this.analytics.capture(ANALYTICS_EVENTS.ERROR_OCCURRED, {
        error_type: err.name,
        error_message: err.message.slice(0, 200),
      });
      this.analytics.captureException(err, { error_context: 'angular' });
    } catch {
      // Analytics must never break error reporting.
    }
    console.error(error);
  }
}

/**
 * Context stamped on every event. Added in before_send rather than register():
 * register() persists across visits (a stale target_app would leak into the
 * next one) and runs only after init has already sent its first events.
 */
function visitProperties(): Record<string, unknown> {
  const params = new URLSearchParams(window.location.search);
  return {
    app: 'login',
    environment: environment.env,
    app_version: environment.version,
    runtime: 'web',
    locale: navigator.language || 'en-GB',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    // Which app sent the user here (the post-login destination).
    target_app: params.get('app'),
    has_customer_context: params.has('customer'),
  };
}

function initAnalytics(): void {
  const posthog = inject(PosthogService);
  const analytics = inject(AnalyticsService);
  const visit = visitProperties();
  const recordingAllowed = !locationCarriesSecrets(window.location);

  const options: Partial<PostHogConfig> = {
    api_host: environment.POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-01-30',
    // Anonymous here; the destination app identifies the user and PostHog
    // merges this visit via the shared *.ricohworkplace.com cookie.
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_exceptions: true,
    // Autocapture only our own login-web.* controls, and never their text or
    // attributes: the continue panel renders the signed-in user's name.
    autocapture: {
      dom_event_allowlist: ['click', 'submit', 'change'],
      css_selector_allowlist: ['[id^="login-web."]'],
    },
    mask_all_text: true,
    mask_all_element_attributes: true,
    rageclick: true,
    enable_heatmaps: true,
    // Magic-link and OAuth-callback page loads carry codes/tokens in the URL,
    // and an app can nest its handoff in `path`; replay records the URL, so
    // those loads aren't recorded at all.
    disable_session_recording: !recordingAllowed,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
      // The recording's page address gets the same scrub as event URLs.
      maskCapturedNetworkRequestFn: (request) => scrubRecordedRequest(request),
    },
    // Network timing would record provider/OIDC request URLs; vitals are enough.
    capture_performance: { web_vitals: true, network_timing: false },
    before_send: (event) => {
      if (!event) return event;
      // Visit context wins over anything the SDK has persisted.
      event.properties = { ...event.properties, ...visit };
      return scrubEventUrls(event);
    },
  };

  // Without the consent banner (CONSENT_ENABLED off), PostHog starts at once,
  // as it did before the banner existed.
  if (!environment.CONSENT_ENABLED) {
    posthog.init(environment.POSTHOG_TOKEN, options);
    return;
  }

  const captureChange: ConsentListener = (state, { first }) =>
    analytics.capture(ANALYTICS_EVENTS.CONSENT_CHANGED, {
      analytics: state.analytics,
      recordings: state.recordings,
      reports: state.reports,
      revision: state.revision,
      first_choice: first,
      consent_id: state.consentId,
    });

  // With the banner, PostHog waits for a choice: even in on_reject mode, init
  // fetches remote config and posts /flags with an anonymous id. Accepting
  // uses cookies as usual; rejecting switches to anonymous cookieless capture
  // (cookieless mode is on in the PostHog project). Recording starts off and
  // follows the Session recordings choice; secret-bearing loads never record.
  // The package installs our before_send behind its own, and sends each later
  // change to captureChange after applying it, so a rejection goes out
  // cookieless rather than with the identified id. Not awaited: it resolves
  // only once there's a choice, and bootstrap must not wait for the banner.
  void inject(ConsentService).startPostHog(
    (beforeSend) => {
      posthog.init(environment.POSTHOG_TOKEN, {
        ...options,
        cookieless_mode: 'on_reject',
        disable_session_recording: true,
        before_send: beforeSend,
      });
      return posthog.posthog;
    },
    { recordingAllowed, beforeSend: options.before_send, afterChange: captureChange },
  );
}

export function provideAnalytics(): (Provider | EnvironmentProviders)[] {
  return [
    provideAppInitializer(initAnalytics),
    { provide: ErrorHandler, useClass: AnalyticsErrorHandler },
  ];
}
